/**
 * ScorecardPage — full-page proctored interview report.
 * Compact hero (ring score) -> skill overview (radar + collapsible detail) ->
 * proctoring (collapsible) -> question-by-question breakdown as an accordion
 * (collapsed by default) -> PDF download. Collapsing detail by default keeps
 * long interviews scannable instead of one huge wall of expanded cards.
 */

import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  ArrowLeftIcon, ArrowDownTrayIcon, ArrowPathIcon, CheckCircleIcon,
  UserCircleIcon, BriefcaseIcon, CalendarIcon, MapPinIcon, GlobeAltIcon,
  VideoCameraIcon, ComputerDesktopIcon, ExclamationTriangleIcon, XCircleIcon,
  UsersIcon, DevicePhoneMobileIcon, EyeSlashIcon, NoSymbolIcon, SpeakerWaveIcon,
  ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, CommandLineIcon, DocumentTextIcon, ChatBubbleLeftRightIcon,
  StarIcon, DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { getScorecard, rescoreSession, downloadScorecardPdf, reviewSession, type ScorecardQuestion, type ProctoringInfo, type Scorecard, type SkillScoreCriterion, type PlagiarismMatch } from '@/services/proctoredInterviewApi'

function scoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'text-gray-400'
  if (score >= 70) return 'text-green-600'
  if (score >= 40) return 'text-amber-600'
  return 'text-red-600'
}

function scoreBarColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'bg-gray-300'
  if (score >= 70) return 'bg-green-500'
  if (score >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

function scoreHex(score: number | null | undefined): string {
  if (score === null || score === undefined) return '#d1d5db'
  if (score >= 70) return '#22c55e'
  if (score >= 40) return '#f59e0b'
  return '#ef4444'
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return value
  }
}

// The scoring prompt requires every coding question's summary to end with a
// sentence starting with the exact literal "Process: " prefix (see
// proctored_interview_llm.py) — split it out so the report can show it as
// its own clearly-labeled "Debugging Process" block instead of it being
// buried inside the general code-evaluation paragraph.
function splitProcessNote(summary: string): { main: string; process: string | null } {
  const idx = summary.lastIndexOf('Process: ')
  if (idx === -1) return { main: summary, process: null }
  return { main: summary.slice(0, idx).trim(), process: summary.slice(idx + 'Process: '.length).trim() }
}

// AI-generated summaries come back as prose paragraphs — split them into
// sentences and render as a bullet list so they're easier to scan. A
// single-sentence summary just renders as plain text (a one-item list reads
// worse than no list).
function BulletedText({ text, className }: { text: string; className?: string }) {
  const points = text.split(/(?<=[.!?])\s+(?=[A-Z0-9])/).map((s) => s.trim()).filter(Boolean)
  if (points.length <= 1) return <p className={className}>{text}</p>
  return (
    <ul className={`list-disc pl-4 space-y-1 ${className || ''}`}>
      {points.map((point, i) => <li key={i}>{point}</li>)}
    </ul>
  )
}

/** A rubric criterion's score with its denominator.
 *
 * Criteria are not scored out of 100 individually — each carries a `weight`,
 * its share of the skill's 100 points, and `earned` is how many of those
 * points this candidate got (see _recompute_scores_deterministically). So a
 * criterion worth 30 that the candidate half-satisfied reads "15/30", and the
 * skill's own score is the sum of its criteria's earned points, out of 100.
 *
 * Scorecards produced before weighting existed have neither field; those fall
 * back to the raw 0-100 score, which is what they were actually scored on.
 */
function CriterionScore({ criterion }: { criterion: SkillScoreCriterion }) {
  const { earned, weight, score, assessed } = criterion
  // No question in the bank covered this one, so the candidate never had the
  // chance to demonstrate it. Showing a score here — even 0 — would read as a
  // judgement of the person rather than a gap in the question bank.
  if (assessed === false) {
    return <span className="text-gray-400 italic">Not assessed</span>
  }
  if (typeof earned === 'number' && typeof weight === 'number') {
    // Coloured on the PERCENTAGE of the weight earned, not the raw points —
    // 20/30 is a good result and 20/80 is a poor one, and colouring by the
    // bare number would paint them identically.
    const pct = weight > 0 ? (earned / weight) * 100 : 0
    return (
      <span className={scoreColor(pct)}>
        {earned.toFixed(earned % 1 === 0 ? 0 : 1)}
        <span className="text-gray-400">/{weight.toFixed(weight % 1 === 0 ? 0 : 1)}</span>
      </span>
    )
  }
  return <span className={scoreColor(score)}>{score?.toFixed(0)}<span className="text-gray-400">/100</span></span>
}

const INTEGRITY_EVENT_LABELS: Record<string, string> = {
  multiple_persons: 'Multiple persons detected',
  no_person: 'No person in view',
  electronic_device: 'Electronic device detected',
  looking_away: 'Looking away from screen',
  camera_blocked: 'Camera blocked',
  multiple_audio_sources: 'Multiple audio sources detected',
}

const INTEGRITY_EVENT_ICONS: Record<string, React.FC<{ className?: string }>> = {
  multiple_persons: UsersIcon,
  no_person: UsersIcon,
  electronic_device: DevicePhoneMobileIcon,
  looking_away: EyeSlashIcon,
  camera_blocked: NoSymbolIcon,
  multiple_audio_sources: SpeakerWaveIcon,
}

// Compact-badge labels/icons for the integrity summary card — covers the
// same event_type vocabulary as INTEGRITY_EVENT_LABELS above, plus the two
// non-camera categories the backend folds in from proctoring_metadata
// (fullscreen_exit, auto_submitted). Keys must match ScorecardResponse's
// integrity_breakdown keys exactly (see _build_scorecard_response in
// proctored_interviews.py).
const INTEGRITY_CATEGORY_LABELS: Record<string, string> = {
  ...INTEGRITY_EVENT_LABELS,
  fullscreen_exit: 'Fullscreen Exits',
  auto_submitted: 'Auto-submitted',
}

const INTEGRITY_CATEGORY_ICONS: Record<string, React.FC<{ className?: string }>> = {
  ...INTEGRITY_EVENT_ICONS,
  fullscreen_exit: ComputerDesktopIcon,
  auto_submitted: XCircleIcon,
}

// auto_submitted (the interview was cut short because of violations) and
// fullscreen_exit rank as the most severe categories for badge coloring —
// everything else (camera/mic detections) is a lesser, informational signal.
function severityRank(type: string): number {
  return type === 'auto_submitted' || type === 'fullscreen_exit' ? 0 : 1
}

/** Compact card next to the hero score circle: integrity events broken down
 * by category, a total count, and a short AI-generated narrative. Always
 * rendered — integrity_summary is always populated by the backend (either
 * the AI paragraph, or "No integrity concerns detected." when the total is
 * zero), so there's never an empty/awkward state to special-case here. */
function IntegritySummaryCard({ scorecard }: { scorecard: Scorecard }) {
  const total = scorecard.integrity_total ?? 0
  const entries = Object.entries(scorecard.integrity_breakdown || {})
    // Most severe first (auto-submission is the outcome that actually affected
    // the interview; fullscreen exits are a close second), so a recruiter
    // scanning the badges sees what mattered most without reading every one.
    .sort(([a], [b]) => severityRank(a) - severityRank(b))

  const badges = entries.map(([type, count]) => {
    const Icon = INTEGRITY_CATEGORY_ICONS[type] || ExclamationTriangleIcon
    const severe = severityRank(type) === 0
    return (
      <span
        key={type}
        className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
          severe ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
        }`}
      >
        <Icon className="w-2.5 h-2.5 flex-shrink-0" />
        {INTEGRITY_CATEGORY_LABELS[type] || type.replace(/_/g, ' ')}: {count}
      </span>
    )
  })

  return (
    <Section
      title="Integrity Events"
      icon={ShieldCheckIcon}
      summary={
        <span className={`text-sm font-bold leading-none whitespace-nowrap ${total > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {total} total event{total === 1 ? '' : 's'}
        </span>
      }
      peek={badges.length > 0 && <div className="flex flex-wrap justify-end gap-1.5">{badges}</div>}
    >
      {scorecard.integrity_summary && (
        <BulletedText text={scorecard.integrity_summary} className="text-xs text-gray-600 leading-relaxed" />
      )}
    </Section>
  )
}

/** Compact card, same shape/placement as IntegritySummaryCard: whether ANY
 * question in this session was flagged as a likely code match against
 * another completed candidate for the same job (see
 * check_plagiarism_for_session / ScorecardResponse.plagiarism_matches).
 * Symmetric — this candidate shows up here whether they triggered the check
 * or were matched against by a later candidate's check. */
function PlagiarismSummaryCard({ scorecard }: { scorecard: Scorecard }) {
  const matches = scorecard.plagiarism_matches || []
  const flagged = matches.length > 0
  const questionLabel = (questionId: string) =>
    scorecard.questions.find((q) => q.question_id === questionId)?.skill_name || 'a question'

  return (
    <Section
      title="Copied Code Check"
      icon={DocumentDuplicateIcon}
      summary={
        <span className={`text-sm font-bold leading-none whitespace-nowrap ${flagged ? 'text-red-600' : 'text-green-600'}`}>
          Copied Code Detected: {flagged ? 'Yes' : 'No'}
        </span>
      }
    >
      {flagged ? (
        <ul className="space-y-1.5 text-xs text-gray-700">
          {matches.map((m, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 whitespace-nowrap">
                {Math.round(m.similarity_score * 100)}% match
              </span>
              with <span className="font-medium text-gray-900">{m.matched_candidate_name}</span>
              on <span className="text-gray-500">{questionLabel(m.question_id)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500">No likely code-copying detected against other candidates for this job.</p>
      )}
    </Section>
  )
}

// Display wording for the candidate's quick-select feedback tags. The stored
// values are stable keys (see FEEDBACK_TAGS in proctored_interview_public.py),
// so this mapping can be reworded without touching stored data. An unknown
// key — from a retired option — falls back to its de-underscored form rather
// than vanishing from the card.
const CANDIDATE_FEEDBACK_LABELS: Record<string, string> = {
  instructions_clear: 'Instructions were clear',
  platform_easy: 'Platform was easy to use',
  questions_relevant: 'Questions were relevant',
  enough_time: 'Had enough time',
  technical_issues: 'Experienced technical issues',
  setup_difficult: 'Camera / mic setup was difficult',
}

// Tags that describe a problem rather than something going well — surfaced in
// amber so a recruiter scanning the card sees complaints before compliments.
const NEGATIVE_FEEDBACK_TAGS = new Set(['technical_issues', 'setup_difficult'])

/** The candidate's own feedback on the assessment experience.
 *
 * Renders nothing when they skipped it — an empty "no feedback given" card
 * would take up as much room as real feedback while saying less. This is
 * about the platform, not the candidate's performance, so it deliberately
 * sits apart from the scoring sections.
 */
function CandidateFeedbackCard({ scorecard }: { scorecard: Scorecard }) {
  const feedback = scorecard.candidate_feedback
  if (!feedback) return null

  const stars = [1, 2, 3, 4, 5].map((star) => (
    star <= feedback.rating
      ? <StarIconSolid key={star} className="w-3.5 h-3.5 text-amber-400" />
      : <StarIcon key={star} className="w-3.5 h-3.5 text-gray-300" />
  ))

  return (
    <Section
      title="Candidate Feedback"
      icon={ChatBubbleLeftRightIcon}
      summary={
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="flex items-center gap-0.5">{stars}</span>
          <span className="text-sm font-bold text-gray-700">{feedback.rating}/5</span>
        </span>
      }
      peek={feedback.tags.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {feedback.tags.map((tag) => (
            <span
              key={tag}
              className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                NEGATIVE_FEEDBACK_TAGS.has(tag) ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {CANDIDATE_FEEDBACK_LABELS[tag] || tag.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    >
      {feedback.comment
        ? <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{feedback.comment}</p>
        : <p className="text-xs text-gray-400">No additional comment.</p>}
      <p className="text-[11px] text-gray-400 mt-2">Submitted {formatDate(feedback.submitted_at)}</p>
    </Section>
  )
}

/** Small collapsible card shell shared by Proctoring / Skills / Q&A sections. */
function Section({
  title, icon: Icon, summary, peek, defaultOpen = false, children,
}: {
  title: string
  icon: React.FC<{ className?: string }>
  summary?: React.ReactNode
  // Optional second row, always visible regardless of collapsed state — for
  // content worth seeing at a glance (e.g. Integrity's category badges)
  // without forcing the whole card open.
  peek?: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {summary}
          {open ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {peek && <div className="px-5 pb-4 pt-1">{peek}</div>}
      {open && <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>}
    </div>
  )
}

/** Compact SVG radar chart giving an at-a-glance shape of skill performance. */
// Radar axis labels: wrapped onto up to 3 lines (never cut mid-name at a fixed
// width), anchored away from the chart so side labels grow outward, and
// stacked upward/downward for the top/bottom axes so nothing overlaps the
// chart. The full name is always available as a hover tooltip.
const RADAR_LABEL_CHARS = 14
const RADAR_LABEL_LINES = 3
const RADAR_LABEL_LINE_HEIGHT = 11

function wrapRadarLabel(name: string): string[] {
  const words = name.trim().split(/\s+/).flatMap((w) => {
    const parts: string[] = []
    for (let i = 0; i < w.length; i += RADAR_LABEL_CHARS) parts.push(w.slice(i, i + RADAR_LABEL_CHARS))
    return parts
  })
  const lines: string[] = []
  for (const word of words) {
    const last = lines[lines.length - 1]
    if (last !== undefined && `${last} ${word}`.length <= RADAR_LABEL_CHARS) lines[lines.length - 1] = `${last} ${word}`
    else lines.push(word)
  }
  if (lines.length > RADAR_LABEL_LINES) {
    const kept = lines.slice(0, RADAR_LABEL_LINES)
    kept[RADAR_LABEL_LINES - 1] = `${kept[RADAR_LABEL_LINES - 1].slice(0, RADAR_LABEL_CHARS - 1)}…`
    return kept
  }
  return lines
}

function RadarLabel({ name, x, y, center }: { name: string; x: number; y: number; center: number }) {
  const lines = wrapRadarLabel(name)
  const anchor = Math.abs(x - center) < 4 ? 'middle' : x > center ? 'start' : 'end'
  const span = (lines.length - 1) * RADAR_LABEL_LINE_HEIGHT
  // Top axis grows upward, bottom axis grows downward, sides are centred on the point.
  const firstY = y < center - 4 ? y - span : y > center + 4 ? y : y - span / 2
  return (
    <text x={x} textAnchor={anchor} dominantBaseline="middle" fontSize={10} fill="#4b5563">
      <title>{name}</title>
      {lines.map((line, i) => (
        <tspan key={i} x={x} y={firstY + i * RADAR_LABEL_LINE_HEIGHT}>{line}</tspan>
      ))}
    </text>
  )
}

function SkillRadarChart({ skills }: { skills: [string, number][] }) {
  const n = skills.length
  if (n < 3) return null
  const size = 340
  const center = size / 2
  const maxR = 80
  const angleStep = (2 * Math.PI) / n
  const pointFor = (i: number, r: number): [number, number] => {
    const angle = angleStep * i - Math.PI / 2
    return [center + r * Math.cos(angle), center + r * Math.sin(angle)]
  }
  const dataPoints = skills.map(([, score], i) => pointFor(i, maxR * (Math.max(0, Math.min(100, score ?? 0)) / 100)))
  const dataPath = `${dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}Z`

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[340px] mx-auto overflow-visible">
      {[0.25, 0.5, 0.75, 1].map((r) => {
        const ringPoints = skills.map((_, i) => pointFor(i, maxR * r))
        const d = `${ringPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}Z`
        return <path key={r} d={d} fill="none" stroke="#e5e7eb" strokeWidth={1} />
      })}
      {skills.map((_, i) => {
        const [x, y] = pointFor(i, maxR)
        return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke="#e5e7eb" strokeWidth={1} />
      })}
      <path d={dataPath} fill="rgba(59,130,246,0.16)" stroke="#3b82f6" strokeWidth={1.5} />
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill="#3b82f6" />
      ))}
      {skills.map(([name], i) => {
        const [x, y] = pointFor(i, maxR + 12)
        return <RadarLabel key={name} name={name} x={x} y={y} center={center} />
      })}
    </svg>
  )
}

function ProctoringSection({ proctoring }: { proctoring?: ProctoringInfo }) {
  if (!proctoring) return <p className="text-sm text-gray-400">No proctoring data was recorded for this session.</p>
  const hasAnything =
    proctoring.ipv4 || proctoring.ipv6 || proctoring.location || proctoring.ip_location || proctoring.camera_snapshot ||
    proctoring.camera_mic_verified || proctoring.fullscreen_violations > 0 || proctoring.declaration_confirmed_at

  if (!hasAnything) return <p className="text-sm text-gray-400">No proctoring data was recorded for this session.</p>

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-6">
        {proctoring.camera_snapshot && (
          <div className="flex-shrink-0">
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Camera Snapshot</p>
            <img
              src={proctoring.camera_snapshot}
              alt="Candidate camera snapshot at start"
              className="w-36 aspect-video object-cover rounded-lg border border-gray-200 bg-black"
            />
          </div>
        )}

        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <ProctoringRow
            icon={VideoCameraIcon}
            label="Camera & Microphone"
            value={proctoring.camera_mic_verified ? 'Verified' : 'Not verified'}
            tone={proctoring.camera_mic_verified ? 'good' : 'bad'}
          />
          <ProctoringRow icon={GlobeAltIcon} label="IPv4 Address" value={proctoring.ipv4 || '—'} />
          <ProctoringRow
            icon={MapPinIcon}
            label="IP Location"
            value={
              proctoring.ip_location
                ? proctoring.ip_location.place_name || 'Location unavailable'
                : 'Location unavailable'
            }
          />
          <ProctoringRow icon={GlobeAltIcon} label="IPv6 Address" value={proctoring.ipv6 || '—'} />
          <ProctoringRow
            icon={MapPinIcon}
            label="Location"
            value={
              proctoring.location
                // place_name is best-effort reverse geocoding — fall back to
                // raw coordinates only when it's missing/failed.
                ? (proctoring.location.place_name || `${proctoring.location.lat.toFixed(4)}, ${proctoring.location.lng.toFixed(4)}`)
                : 'Not shared'
            }
          />
          <ProctoringRow
            icon={ComputerDesktopIcon}
            label="Extra Display"
            value={
              proctoring.extra_monitor_detected === true ? 'Detected'
                : proctoring.extra_monitor_detected === false ? 'None detected'
                : 'Not checked'
            }
            tone={proctoring.extra_monitor_detected === true ? 'bad' : undefined}
          />
          <ProctoringRow
            icon={proctoring.fullscreen_violations > 0 ? ExclamationTriangleIcon : CheckCircleIcon}
            label="Fullscreen Exits"
            value={String(proctoring.fullscreen_violations)}
            tone={proctoring.fullscreen_violations > 0 ? 'bad' : 'good'}
          />
          <ProctoringRow
            icon={proctoring.auto_submitted_due_to_violations ? XCircleIcon : CheckCircleIcon}
            label="Auto-submitted"
            value={proctoring.auto_submitted_due_to_violations ? 'Yes — 3 fullscreen exits' : 'No'}
            tone={proctoring.auto_submitted_due_to_violations ? 'bad' : undefined}
          />
        </div>
      </div>

      {/*
        DB-IP's free City Lite database is CC BY 4.0, which requires a link
        back to db-ip.com on any page displaying results from it. That
        attribution is NOT currently rendered here — a deliberate product
        decision to keep the scorecard clean, which leaves the free tier's
        attribution requirement UNMET.

        This is a licensing gap, not a bug, and it is not fixed by any code
        change on this screen. To close it properly, either render the
        attribution below, or move to DB-IP's commercial IP to Location
        database, which carries no attribution requirement.

        Do not remove this comment when the attribution is eventually
        restored — it records why it is absent.
      */}

      {proctoring.recording_url && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-2">Screen & Audio Recording</p>
          <video
            src={proctoring.recording_url}
            controls
            preload="metadata"
            className="w-full max-w-2xl rounded-lg border border-gray-200 bg-black"
          />
        </div>
      )}

      {proctoring.integrity_events.length > 0 && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-2">
            Camera/Mic Integrity Events ({proctoring.integrity_events.length})
          </p>
          <div className="max-h-44 overflow-y-auto flex flex-col gap-1.5">
            {proctoring.integrity_events.map((event, idx) => {
              const Icon = INTEGRITY_EVENT_ICONS[event.type] || ExclamationTriangleIcon
              return (
                <div key={idx} className="flex items-center gap-2.5 text-sm">
                  <Icon className="w-4 h-4 flex-shrink-0 text-amber-500" />
                  <span className="text-gray-700">{INTEGRITY_EVENT_LABELS[event.type] || event.type}</span>
                  <span className="text-xs text-gray-400 ml-auto">{formatDate(event.detected_at)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
        These signals come from the candidate's own browser (location and camera require the candidate's
        permission) — not a substitute for in-person proctoring.
      </p>
    </div>
  )
}

function ProctoringRow({
  icon: Icon, label, value, tone,
}: { icon: React.FC<{ className?: string }>; label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className={`w-4 h-4 flex-shrink-0 ${tone === 'bad' ? 'text-red-500' : tone === 'good' ? 'text-green-500' : 'text-gray-400'}`} />
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className={`text-sm font-medium ${tone === 'bad' ? 'text-red-600' : 'text-gray-800'}`}>{value}</p>
      </div>
    </div>
  )
}

export default function ScorecardPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [downloading, setDownloading] = React.useState(false)
  const [expandedSkills, setExpandedSkills] = React.useState<Set<string>>(new Set())
  const [expandedQuestions, setExpandedQuestions] = React.useState<Set<string>>(new Set())

  const { data: scorecard, isLoading } = useQuery({
    queryKey: ['proctored-interview-scorecard', sessionId],
    queryFn: () => getScorecard(sessionId!),
    enabled: !!sessionId,
    refetchOnMount: 'always',
  })

  // Which decision the recruiter is currently confirming a reason for, or
  // null when the modal is closed. Holding the intended action here (rather
  // than a bare `open` flag) keeps the modal's copy and the eventual request
  // in step — there is no way to open it for Approve and submit a Reject.
  const [reviewAction, setReviewAction] = React.useState<'approved' | 'rejected' | null>(null)
  const [reviewReason, setReviewReason] = React.useState('')

  const reviewMutation = useMutation({
    mutationFn: ({ status, reason }: { status: 'approved' | 'rejected'; reason: string }) =>
      reviewSession(sessionId!, status, reason),
    onSuccess: (updated) => {
      // The endpoint returns the refreshed scorecard, so write it straight
      // into the cache instead of waiting on a refetch round-trip.
      queryClient.setQueryData(['proctored-interview-scorecard', sessionId], updated)
      // The decision also moves the candidate's pipeline stage and Stage
      // Acknowledgement state — mark those (and the job's interview lists)
      // stale so Apply Matching and the interview tabs show it without a reload.
      for (const key of ['stage-acknowledgements', 'comparison-results', 'live-interview-sessions', 'live-interview-candidates', 'proctored-interview-submissions']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      setReviewAction(null)
      setReviewReason('')
      toast.success(updated.review_status === 'approved' ? 'Candidate approved' : 'Candidate rejected')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to record the decision'),
  })

  const rescoreMutation = useMutation({
    mutationFn: () => rescoreSession(sessionId!),
    onSuccess: () => {
      toast.success('Score recalculated')
      queryClient.invalidateQueries({ queryKey: ['proctored-interview-scorecard', sessionId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to recalculate score'),
  })

  const handleDownloadPdf = async () => {
    if (!sessionId) return
    setDownloading(true)
    try {
      await downloadScorecardPdf(sessionId, scorecard?.candidate_name)
    } catch {
      toast.error('Failed to download PDF')
    } finally {
      setDownloading(false)
    }
  }

  const toggleSkill = (name: string) => {
    setExpandedSkills((prev) => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  const toggleQuestion = (id: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!scorecard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Scorecard not found.</p>
      </div>
    )
  }

  const notScoredYet = !scorecard.scored_at
  const hasReviewDecision = !!scorecard.review_status
  const skillEntries: [string, number][] = Object.entries(scorecard.skill_scores || {}).map(([name, data]) => [name, data.score ?? 0])
  const allQuestionIds = scorecard.questions?.map((q) => q.question_id) || []
  const allExpanded = allQuestionIds.length > 0 && allQuestionIds.every((id) => expandedQuestions.has(id))
  const overallPct = Math.max(0, Math.min(100, scorecard.overall_score ?? 0))
  const violationCount = scorecard.proctoring?.fullscreen_violations ?? 0
  const integrityCount = scorecard.proctoring?.integrity_events?.length ?? 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Back and Recalculate together on the left: both are about
              navigating/refreshing this view, as opposed to the decision and
              export actions on the right. */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="w-4 h-4" /> Back
            </button>
            {/* Only offered until a score exists — once scoring has run
                (scored_at set, even for a genuine 0%), the score is final.
                Still shown if scoring hasn't run yet or failed, so it can be
                retried. */}
            {!scorecard.scored_at && (
              <button
                onClick={() => rescoreMutation.mutate()}
                // An interrupted assessment is scoreable too — it still has
                // whatever the candidate managed before it stopped.
                disabled={
                  rescoreMutation.isPending
                  || (scorecard.status !== 'completed' && scorecard.status !== 'incomplete')
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 ${rescoreMutation.isPending ? 'animate-spin' : ''}`} />
                {rescoreMutation.isPending ? 'Recalculating…' : 'Recalculate Score'}
              </button>
            )}
          </div>
          {!hasReviewDecision && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReviewAction('approved')}
                disabled={notScoredYet || reviewMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-700 rounded-md text-sm font-medium hover:bg-green-50 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => setReviewAction('rejected')}
                disabled={notScoredYet || reviewMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-50"
              >
                <XCircleIcon className="w-4 h-4" /> Reject
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={downloading || notScoredYet}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="w-4 h-4" /> {downloading ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>
          )}
          {hasReviewDecision && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloading || notScoredYet}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" /> {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {notScoredYet ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <LoadingSpinner size="lg" />
            <p className="text-sm text-gray-500 mt-4">
              {scorecard.status === 'completed' || scorecard.status === 'incomplete'
                ? 'Scoring is in progress — this usually takes under a minute. Refresh shortly.'
                : 'This candidate has not completed the interview yet.'}
            </p>
          </div>
        ) : (
          <>
            {/* An interrupted session is scored only on the questions the
                candidate actually reached, so the headline score below is an
                average over those alone. Shown before the score, not beside
                it, so the number is never read without its denominator. */}
            {scorecard.status === 'incomplete' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-amber-900 mb-1">Partial result — assessment interrupted</p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  This candidate answered{' '}
                  <span className="font-semibold">
                    {scorecard.questions_answered} of {scorecard.questions_total}
                  </span>{' '}
                  questions before the assessment ended unexpectedly
                  {typeof scorecard.skills_assessed === 'number'
                    && typeof scorecard.skills_total === 'number'
                    && scorecard.skills_assessed < scorecard.skills_total && (
                    <>
                      , covering{' '}
                      <span className="font-semibold">
                        {scorecard.skills_assessed} of {scorecard.skills_total}
                      </span>{' '}
                      skills
                    </>
                  )}
                  . The score reflects only what was actually assessed — skills no question
                  reached are excluded rather than counted as zero, so this is not comparable
                  with a completed assessment.
                </p>
              </div>
            )}
            {/* Overview — compact hero with ring score, plus the integrity
                summary card shown below it (full width, not a narrow sidebar) */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 min-w-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {(scorecard.candidate_name || '?').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-base font-semibold text-gray-900 truncate">{scorecard.candidate_name || 'Unnamed Candidate'}</p>
                        {/* The recruiter's decision, sitting with the
                            candidate's name rather than among the scores —
                            it is a human judgement about this person, not
                            another number the system produced. */}
                        {scorecard.review_status && (
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                              scorecard.review_status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                            title={
                              scorecard.reviewed_by_name
                                ? `${scorecard.review_status === 'approved' ? 'Approved' : 'Rejected'} by ${scorecard.reviewed_by_name} on ${formatDate(scorecard.reviewed_at)}`
                                : undefined
                            }
                          >
                            {scorecard.review_status === 'approved' ? 'Approved' : 'Rejected'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                        <span className="inline-flex items-center gap-1"><BriefcaseIcon className="w-3.5 h-3.5" />{scorecard.job_title || 'Unknown Position'}</span>
                        <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" />{formatDate(scorecard.completed_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div
                      className="relative w-[72px] h-[72px] rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `conic-gradient(${scoreHex(scorecard.overall_score)} ${overallPct}%, #eef0f4 0)` }}
                    >
                      <div className="absolute inset-[5px] rounded-full bg-white flex flex-col items-center justify-center leading-none">
                        {/* Shown as a percentage rather than "n/100": the ring
                            around it already encodes the same value as a
                            proportion, so a denominator underneath is
                            redundant where "%" says it in one character. */}
                        <span className={`text-xl font-bold ${scoreColor(scorecard.overall_score)}`}>
                          {scorecard.overall_score !== null && scorecard.overall_score !== undefined
                            ? `${scorecard.overall_score.toFixed(0)}%`
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <IntegritySummaryCard scorecard={scorecard} />
              <PlagiarismSummaryCard scorecard={scorecard} />

              {scorecard.summary && (
                <Section title="Summary" icon={DocumentTextIcon}>
                  <BulletedText text={scorecard.summary} className="text-sm text-gray-700 leading-relaxed" />
                </Section>
              )}
            </div>

            {/* Skills & Rubric Scores */}
            {skillEntries.length > 0 && (
              <Section title="Skill Breakdown" icon={CheckCircleIcon} defaultOpen>
                <div className={`grid gap-6 ${skillEntries.length >= 3 ? 'md:grid-cols-[300px_1fr]' : ''}`}>
                  {skillEntries.length >= 3 && (
                    <div className="flex items-center justify-center">
                      <SkillRadarChart skills={skillEntries} />
                    </div>
                  )}
                  <div className="space-y-2">
                    {Object.entries(scorecard.skill_scores || {}).map(([skillName, data]) => {
                      const isOpen = expandedSkills.has(skillName)
                      const hasCriteria = (data.criteria || []).length > 0
                      return (
                        <div key={skillName} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => hasCriteria && toggleSkill(skillName)}
                            className={`w-full text-left px-3.5 py-2.5 ${hasCriteria ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-semibold text-gray-900">{skillName}</span>
                              <div className="flex items-center gap-2">
                                {/* Only shown when the question bank covered
                                    some but not all of this skill's criteria —
                                    the score is then real but rests on partial
                                    evidence, and reading it as a full
                                    evaluation of the skill would overstate it. */}
                                {typeof data.criteria_assessed === 'number'
                                  && typeof data.criteria_total === 'number'
                                  && data.criteria_assessed < data.criteria_total && (
                                  <span
                                    className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap"
                                    title="Not every rubric criterion for this skill was covered by a question. The score reflects only the criteria that were assessed."
                                  >
                                    {data.criteria_assessed} of {data.criteria_total} assessed
                                  </span>
                                )}
                                <span className={`text-sm font-bold ${scoreColor(data.score)}`}>
                                  {data.score?.toFixed(0)}%
                                </span>
                                {hasCriteria && (isOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />)}
                              </div>
                            </div>
                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${scoreBarColor(data.score)}`}
                                style={{ width: `${Math.max(0, Math.min(100, data.score ?? 0))}%` }}
                              />
                            </div>
                          </button>
                          {isOpen && hasCriteria && (
                            <div className="px-3.5 pb-3 space-y-2 border-t border-gray-100 pt-2.5">
                              {(data.criteria || []).map((c, idx) => (
                                <div key={idx} className="text-xs">
                                  <div className="flex items-center justify-between text-gray-700">
                                    <span className="font-medium">{c.criterion}</span>
                                    <CriterionScore criterion={c} />
                                  </div>
                                  {c.feedback && <p className="text-gray-500 mt-0.5">{c.feedback}</p>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </Section>
            )}

            {/* Proctoring */}
            <Section
              title="Proctoring"
              icon={ShieldCheckIcon}
              summary={
                <div className="hidden sm:flex items-center gap-1.5">
                  {violationCount > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">{violationCount} exit{violationCount === 1 ? '' : 's'}</span>
                  )}
                  {integrityCount > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{integrityCount} flag{integrityCount === 1 ? '' : 's'}</span>
                  )}
                  {violationCount === 0 && integrityCount === 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700">Clean</span>
                  )}
                </div>
              }
            >
              <ProctoringSection proctoring={scorecard.proctoring} />
            </Section>

            {/* Questions & Answers */}
            <Section
              title="Interview Questions & Answers"
              icon={ChatBubbleLeftRightIcon}
              summary={<span className="text-xs text-gray-400">{allQuestionIds.length} question{allQuestionIds.length === 1 ? '' : 's'}</span>}
            >
              <div className="flex items-center justify-end mb-3">
                <button
                  type="button"
                  onClick={() => setExpandedQuestions(allExpanded ? new Set() : new Set(allQuestionIds))}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
              <div className="space-y-2">
                {scorecard.questions.map((q, idx) => (
                  <QuestionCard
                    key={q.question_id}
                    question={q}
                    index={idx}
                    isOpen={expandedQuestions.has(q.question_id)}
                    onToggle={() => toggleQuestion(q.question_id)}
                    plagiarismMatches={(scorecard.plagiarism_matches || []).filter(
                      (m) => m.question_id === q.question_id
                    )}
                  />
                ))}
              </div>
            </Section>

            {/* Last on the page: this is the candidate's opinion of the
                platform, not an input to judging them, so it sits after
                everything that is. Deliberately excluded from the PDF
                export for the same reason. */}
            <CandidateFeedbackCard scorecard={scorecard} />
          </>
        )}
      </div>

      <ReviewReasonModal
        action={reviewAction}
        reason={reviewReason}
        onReasonChange={setReviewReason}
        submitting={reviewMutation.isPending}
        onCancel={() => { setReviewAction(null); setReviewReason('') }}
        onConfirm={() => {
          if (reviewAction) reviewMutation.mutate({ status: reviewAction, reason: reviewReason })
        }}
      />
    </div>
  )
}

/** Collects the reason for an Approve/Reject before it is recorded.
 *
 * A reason is required in both directions. An approval nobody can explain
 * later is as awkward to defend as an unexplained rejection, and this is a
 * hiring decision that may have to be justified to the candidate or to
 * whoever audits the process.
 */
export function ReviewReasonModal({
  action, reason, onReasonChange, submitting, onCancel, onConfirm,
}: {
  action: 'approved' | 'rejected' | null
  reason: string
  onReasonChange: (v: string) => void
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // Focus WITHOUT scrolling. `autoFocus` (and a plain .focus()) makes the
  // browser scroll the focused element into view, which on a long scorecard
  // yanks the page to the bottom the instant Approve/Reject is clicked. The
  // modal is viewport-fixed and already on screen, so there is nothing to
  // scroll to — preventScroll keeps the page exactly where the recruiter
  // left it.
  React.useEffect(() => {
    if (!action) return
    textareaRef.current?.focus({ preventScroll: true })
  }, [action])

  // Esc closes, and the page behind is frozen while the modal is up so a
  // stray scroll doesn't move the report underneath it.
  React.useEffect(() => {
    if (!action) return
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [action, onCancel])

  if (!action) return null
  const approving = action === 'approved'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-1.5">
          {approving
            ? <CheckCircleIcon className="w-5 h-5 text-green-600" />
            : <XCircleIcon className="w-5 h-5 text-red-600" />}
          <h2 className="text-base font-semibold text-gray-900">
            {approving ? 'Approve candidate' : 'Reject candidate'}
          </h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {approving
            ? 'Record why this candidate is being approved. This is kept with the assessment.'
            : 'Record why this candidate is being rejected. This is kept with the assessment.'}
        </p>

        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={approving
            ? 'e.g. Strong on system design and debugging; comfortably above the bar for this role.'
            : 'e.g. Did not demonstrate the required depth in the core skills for this role.'}
          rows={4}
          maxLength={2000}
          ref={textareaRef}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 mb-4"
        />

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting || !reason.trim()}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 ${
              approving ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {submitting ? <LoadingSpinner size="sm" /> : null}
            {submitting ? 'Saving…' : approving ? 'Approve' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDuration(seconds?: number | null): string | null {
  if (seconds === null || seconds === undefined) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function QuestionCard({
  question, index, isOpen, onToggle, plagiarismMatches = [],
}: {
  question: ScorecardQuestion
  index: number
  isOpen: boolean
  onToggle: () => void
  // This question's likely-copying matches, pre-filtered by the caller from
  // scorecard.plagiarism_matches (question_id === this question's id).
  plagiarismMatches?: PlagiarismMatch[]
}) {
  const typeLabel = question.question_subtype_name
    ? `${question.question_type_name} · ${question.question_subtype_name}`
    : question.question_type_name
  const timeSpent = formatDuration(question.time_spent_seconds)
  const isCoding = question.test_cases.length > 0
  const [showRunHistory, setShowRunHistory] = React.useState(false)
  const [expandedRuns, setExpandedRuns] = React.useState<Set<number>>(new Set())
  const toggleRun = (idx: number) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full flex-shrink-0">{question.skill_name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{typeLabel}</span>
              {timeSpent && <span className="text-xs text-gray-400 flex-shrink-0">· {timeSpent}</span>}
              {plagiarismMatches.length > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-red-50 text-red-700 rounded-full flex-shrink-0"
                  title={plagiarismMatches
                    .map((m) => `${Math.round(m.similarity_score * 100)}% match with ${m.matched_candidate_name}`)
                    .join(', ')}
                >
                  <ExclamationTriangleIcon className="w-3 h-3" />
                  Possible Copied Code
                </span>
              )}
            </div>
            <p className={`text-sm font-medium text-gray-900 ${isOpen ? '' : 'truncate'}`} title={`Q${index + 1}. ${question.question_text}`}>
              Q{index + 1}. {question.question_text}
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {/* Per-answer score is the mean of that answer's own raw 0-100
                criterion judgements, so it is out of 100 — unlike the
                criterion rows inside a skill, which are out of their weight. */}
            {question.answer_score !== null && question.answer_score !== undefined && (
              <span className={`text-sm font-bold ${scoreColor(question.answer_score)}`}>
                {question.answer_score.toFixed(0)}<span className="text-xs font-normal text-gray-400">/100</span>
              </span>
            )}
            {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-gray-100">
          {plagiarismMatches.length > 0 && (
            <div className="mb-3 mt-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                Possible Copied Code
              </p>
              <ul className="text-xs text-red-700 space-y-0.5">
                {plagiarismMatches.map((m, i) => (
                  <li key={i}>
                    {Math.round(m.similarity_score * 100)}% match with{' '}
                    <span className="font-medium">{m.matched_candidate_name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {question.answer_whiteboard_image && (
            <div className="mb-3 mt-2">
              <p className="text-xs font-semibold text-gray-500 mb-1">Candidate's Whiteboard</p>
              <img
                src={question.answer_whiteboard_image}
                alt="Candidate's whiteboard sketch"
                className="w-full border border-gray-200 rounded-lg bg-white"
              />
            </div>
          )}

          <div className="mb-3 mt-2">
            <p className="text-xs font-semibold text-gray-500 mb-1">
              {isCoding ? "Candidate's Code" : question.answer_whiteboard_image ? 'Notes' : "Candidate's Answer"}
            </p>
            {question.answer_text ? (
              isCoding ? (
                <pre className="text-xs text-gray-100 bg-[#1e1e1e] rounded-lg px-3.5 py-3 overflow-x-auto font-mono leading-relaxed">
                  {question.answer_text}
                </pre>
              ) : (
                <blockquote className="text-sm text-gray-700 bg-gray-50 border-l-2 border-gray-300 rounded-r-md px-3 py-2 italic">
                  {question.answer_text}
                </blockquote>
              )
            ) : !question.answer_whiteboard_image ? (
              <p className="text-sm text-gray-400 italic">No answer submitted</p>
            ) : null}
          </div>

          {isCoding && question.answer_test_case_results.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 mb-1">Test Case Results</p>
              <div className="space-y-1">
                {question.answer_test_case_results.map((r, idx) => (
                  <div key={idx} className="flex items-start gap-1.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${r.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {r.passed ? 'PASS' : 'FAIL'}
                    </span>
                    <span className="text-gray-600">{r.description}{r.explanation ? ` — ${r.explanation}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {question.answer_summary && (() => {
            const { main, process } = isCoding ? splitProcessNote(question.answer_summary) : { main: question.answer_summary, process: null }
            return (
              <>
                <div className="flex items-start gap-1.5 mb-3">
                  <CheckCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    {isCoding && <p className="text-xs font-semibold text-gray-500">Code Evaluation Summary</p>}
                    <BulletedText text={main} className="text-xs text-gray-600" />
                  </div>
                </div>
                {process && (
                  <div className="flex items-start gap-1.5 mb-3 bg-indigo-50 rounded-lg px-3 py-2">
                    <CommandLineIcon className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-indigo-700">Debugging Process</p>
                      <p className="text-xs text-indigo-900">{process}</p>
                    </div>
                  </div>
                )}
              </>
            )
          })()}

          {isCoding && question.answer_run_history && question.answer_run_history.length > 0 && (
            <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowRunHistory((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
              >
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <CommandLineIcon className="w-3.5 h-3.5 text-gray-400" />
                  Code Run History ({question.answer_run_history.length} attempt{question.answer_run_history.length === 1 ? '' : 's'})
                </span>
                {showRunHistory ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showRunHistory && (
                <div className="divide-y divide-gray-100">
                  {question.answer_run_history.map((run, idx) => {
                    const isRunOpen = expandedRuns.has(idx)
                    const passCount = run.results?.filter((r) => r.passed).length
                    const totalCount = run.results?.length
                    return (
                      <div key={idx}>
                        <button
                          type="button"
                          onClick={() => toggleRun(idx)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-50 text-left"
                        >
                          <span className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400 font-mono w-5 flex-shrink-0">#{idx + 1}</span>
                            <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${run.action === 'run_tests' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                              {run.action === 'run_tests' ? 'Run Test Cases' : 'Run'}
                            </span>
                            <span className="text-gray-400">{formatDate(run.at)}</span>
                            {totalCount !== undefined && (
                              <span className={passCount === totalCount ? 'text-green-600' : 'text-amber-600'}>
                                {passCount}/{totalCount} passed
                              </span>
                            )}
                            {run.exit_code != null && run.action === 'run' && (
                              <span className={run.exit_code === 0 ? 'text-green-600' : 'text-red-600'}>
                                exit {run.exit_code}
                              </span>
                            )}
                          </span>
                          {isRunOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                        </button>
                        {isRunOpen && (
                          <div className="px-3 pb-2.5">
                            <pre className="text-[11px] text-gray-100 bg-[#1e1e1e] rounded-md px-3 py-2 overflow-x-auto font-mono leading-relaxed mb-1.5">
                              {run.code}
                            </pre>
                            {run.action === 'run_tests' && run.results && run.results.length > 0 && (
                              <div className="space-y-1">
                                {run.results.map((r, ri) => (
                                  <div key={ri} className="flex items-start gap-1.5 text-[11px]">
                                    <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${r.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                      {r.passed ? 'PASS' : 'FAIL'}
                                    </span>
                                    <span className="text-gray-600">{r.description}{r.explanation ? ` — ${r.explanation}` : ''}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {run.action === 'run' && (run.stdout || run.stderr) && (
                              <div className="text-[11px] font-mono space-y-1">
                                {run.stdout && <pre className="text-gray-600 whitespace-pre-wrap">{run.stdout}</pre>}
                                {run.stderr && <pre className="text-red-600 whitespace-pre-wrap">{run.stderr}</pre>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {question.answer_rubric_scores && question.answer_rubric_scores.length > 0 && (
            <div className="pt-3 border-t border-gray-100 space-y-1">
              {question.answer_rubric_scores.map((c, idx) => (
                <div key={idx} className="text-xs">
                  <div className="flex items-center justify-between text-gray-700">
                    <span className="font-medium">{c.criterion}</span>
                    <CriterionScore criterion={c} />
                  </div>
                  {c.feedback && <p className="text-gray-500">{c.feedback}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
